import { useState, useRef, useEffect, useCallback } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Link, router, useForm, usePage } from "@inertiajs/react"
import { ArrowLeft, BadgeInfo, Building2, Check, CheckCircle2, Clock, Copy, Crown, FolderInput, Hash, LogOut, Mail, MoreHorizontal, Pencil, Play, Shield, ShieldCheck, Sparkles, Trash2, UserPlus, Users, X } from "lucide-react"
import { Layout } from "@/components/layouts/Layout"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { FieldTooltip } from "@/components/ui/field-tooltip"
import { Kbd } from "@/components/ui/kbd"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DeleteAgentDialog } from "@/Pages/Agents/DeleteAgentDialog"
import { Editable, EditableArea, EditableCancel, EditableInput, EditablePreview, EditableSubmit, EditableTrigger } from "@/components/ui/editable"

function CopyableEmail({ email }) {
  const [copied, setCopied] = useState(false)

  function handleCopy(e) {
    e.stopPropagation()
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group/copy bg-muted text-slate-500 inline-flex h-5 w-fit items-center gap-1 rounded-sm px-1 text-xs font-medium cursor-pointer transition-colors"
    >
      <span>{email}</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-500 shrink-0" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground group-hover/copy:text-primary shrink-0 transition-colors" />
      )}
    </button>
  )
}

function timeAgo(dateString) {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function roleIcon(role) {
  switch (role) {
    case "owner": return Crown
    case "admin": return ShieldCheck
    default:      return Shield
  }
}

function agentStatusColor(status) {
  switch (status) {
    case "active":   return "bg-green-500"
    case "paused":   return "bg-amber-500"
    case "stopped":  return "bg-red-500"
    default:         return "bg-muted-foreground/40"
  }
}

export default function WorkspacesShow({ workspace, current_membership, memberships, invitations, agents, all_workspaces }) {
  const { errors, current_user } = usePage().props
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [pendingMove, setPendingMove] = useState(null) // { agent, targetWorkspace }
  const [pendingDelete, setPendingDelete] = useState(null) // agent
  const [pendingRemoveMember, setPendingRemoveMember] = useState(null) // membership
  const [pendingLeave, setPendingLeave] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [copiedNewAgent, setCopiedNewAgent] = useState(false)
  const [slugResetKey, setSlugResetKey] = useState(0)
  const [nameResetKey, setNameResetKey] = useState(0)

  useEffect(() => {
    if (errors?.slug) setSlugResetKey(k => k + 1)
  }, [errors?.slug])
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)

  const inviteForm = useForm({ email: "", role: "member" })

  const isOwner = current_membership?.role === "owner"
  const isAdmin = current_membership?.role === "admin"
  const canInvite = isOwner || isAdmin

  function availableRoles() {
    if (isOwner) return [ "member", "admin", "owner" ]
    if (isAdmin) return [ "member", "admin" ]
    return []
  }

  function canManageMembership(membership) {
    if (membership.user_id === current_user?.id) return false
    if (isOwner) return true
    if (isAdmin && membership.role === "member") return true
    return false
  }

  function canAssignRole(role) {
    if (isOwner) return true
    if (isAdmin && (role === "member" || role === "admin")) return true
    return false
  }

  function isLastOwner() {
    const ownerCount = memberships.filter((m) => m.role === "owner").length
    return ownerCount === 1 && current_membership?.role === "owner"
  }
  const newAgentAddress = `new@${workspace.slug}.decodekit.ai`
  const inboxAddress = `inbox@${workspace.slug}.decodekit.ai`
  const testAgentAddress = `test-{agent_id}@${workspace.slug}.decodekit.ai`
  const directRunAddress = `agent-{agent_id}@${workspace.slug}.decodekit.ai`

  const handleCopyNewAgentAddress = useCallback(() => {
    navigator.clipboard.writeText(newAgentAddress).then(() => {
      setCopiedNewAgent(true)
      setTimeout(() => setCopiedNewAgent(false), 2000)
    })
  }, [newAgentAddress])

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  function handleNameSubmit(name) {
    if (!name || !name.trim()) {
      setNameResetKey(k => k + 1)
      return
    }
    if (name !== workspace.name) {
      router.patch(`/workspaces/${workspace.slug}`, { workspace: { name } }, { preserveState: true })
    }
  }

  function handleSlugSubmit(slug) {
    if (slug && slug !== workspace.slug) {
      router.patch(`/workspaces/${workspace.slug}`, { workspace: { slug } })
    }
  }

  function handlePrimaryToggle(checked) {
    router.patch(`/workspaces/${workspace.slug}`, { primary: checked })
  }

  function confirmMove() {
    const { agent, targetWorkspace } = pendingMove
    setPendingMove(null)
    router.patch(`/${workspace.slug}/agents/${agent.id}/move`, { workspace_id: targetWorkspace.id })
  }

  function handleInviteSubmit(e) {
    e.preventDefault()
    inviteForm.post(`/workspaces/${workspace.slug}/invitations`, {
      onSuccess: () => {
        setInviteOpen(false)
        inviteForm.reset()
      }
    })
  }

  function handleRoleChange(membershipId, newRole) {
    router.patch(`/workspaces/${workspace.slug}/memberships/${membershipId}`, { role: newRole })
  }

  function confirmRemoveMember() {
    const membership = pendingRemoveMember
    setPendingRemoveMember(null)
    router.delete(`/workspaces/${workspace.slug}/memberships/${membership.id}`)
  }

  function handleCancelInvitation(invitationId) {
    router.delete(`/workspaces/${workspace.slug}/invitations/${invitationId}`)
  }

  function handleResendInvitation(invitationId) {
    router.post(`/workspaces/${workspace.slug}/invitations/${invitationId}/resend`)
  }

  function confirmLeave() {
    setPendingLeave(false)
    router.delete(`/workspaces/${workspace.slug}/memberships/leave`)
  }

  function formatExpiry(expiresAt) {
    const date = new Date(expiresAt)
    const now = new Date()
    const diffMs = date - now
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays <= 0) return "Expired"
    if (diffDays === 1) return "Expires tomorrow"
    return `Expires in ${diffDays} days`
  }


  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" asChild className="group/back -ml-1 h-7 w-7">
                <Link href="/workspaces">
                  <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover/back:text-foreground" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>All workspaces</TooltipContent>
          </Tooltip>
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">{workspace.name}</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col gap-8 p-6">

          <div ref={nameRef}>
            <Editable
              key={`${workspace.name || ""}-${nameResetKey}`}
              defaultValue={workspace.name}
              onSubmit={handleNameSubmit}
              placeholder="Untitled workspace"
            >
              <div className="flex items-center gap-1">
                <div className="group/editable flex items-center gap-1.5">
                  <EditableArea>
                    <EditablePreview className="text-3xl font-medium" />
                    <EditableInput className="text-3xl font-medium border-none shadow-none focus-visible:ring-0" />
                  </EditableArea>
                  <EditableTrigger asChild>
                    <Button variant="ghost" size="sm" className="group/edit opacity-0 group-hover/editable:opacity-100 transition-all h-7 w-7 p-0 mt-0.25">
                      <Pencil className="h-4 w-4 text-muted-foreground group-hover/edit:text-foreground transition-all" />
                    </Button>
                  </EditableTrigger>
                </div>
                <EditableSubmit asChild>
                  <Button variant="ghost" size="sm" className="group/submit h-7 w-7 p-0 hover:bg-green-100 transition-all">
                    <Check className="h-4 w-4 text-muted-foreground group-hover/submit:text-green-500 transition-all" />
                  </Button>
                </EditableSubmit>
                <EditableCancel asChild>
                  <Button variant="ghost" size="sm" className="group/cancel h-7 w-7 p-0 transition-all">
                    <X className="h-4 w-4 text-muted-foreground group-hover/cancel:text-foreground transition-all" />
                  </Button>
                </EditableCancel>
              </div>
            </Editable>
          </div>

          <div className="max-w-2xl">
            <h3 className="flex items-center gap-1.5 text-base font-medium mb-0.5">
              <BadgeInfo className="size-4 text-muted-foreground" aria-hidden />
              Details
            </h3>
            <p className="text-sm text-muted-foreground mb-3">Basic information about this workspace</p>
            <div className="rounded-lg border divide-y">
              <div className="flex items-center justify-between px-4 min-h-11 py-2">
                <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                  <Hash className="size-4 shrink-0" aria-hidden />
                  <span className="flex items-center gap-1">
                    <span className="text-sm">Slug</span>
                    <FieldTooltip ariaLabel="About the workspace slug">
                      <div className="mb-1 text-sm font-medium">How the slug is used</div>
                      <ul className="list-disc space-y-1 pl-5 text-[13px]">
                        <li>The slug sets the workspace&apos;s email addresses</li>
                        <li>
                          <CopyableEmail email={newAgentAddress} /> creates a new agent
                        </li>
                        <li>
                          <CopyableEmail email={inboxAddress} /> checks incoming email against active agents
                        </li>
                      </ul>
                    </FieldTooltip>
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Editable
                    key={`${workspace.slug}-${slugResetKey}`}
                    defaultValue={workspace.slug}
                    onSubmit={handleSlugSubmit}
                    placeholder="workspace-slug"
                    autosize
                  >
                    <div className="flex items-center gap-1">
                      <div className="group/editable flex items-center gap-1">
                        <EditableTrigger asChild>
                          <Button variant="ghost" size="sm" className="group/edit opacity-0 group-hover/editable:opacity-100 transition-all h-6 w-6 p-0">
                            <Pencil className="size-3.5 text-muted-foreground group-hover/edit:text-foreground transition-all" />
                          </Button>
                        </EditableTrigger>
                        <EditableArea>
                          <EditablePreview className="text-sm" />
                          <EditableInput className="text-sm border-none shadow-none focus-visible:ring-0" />
                        </EditableArea>
                      </div>
                      <EditableSubmit asChild>
                        <Button variant="ghost" size="sm" className="group/submit h-6 w-6 p-0 hover:bg-green-100 transition-all">
                          <Check className="h-3.5 w-3.5 text-muted-foreground group-hover/submit:text-green-500 transition-all" />
                        </Button>
                      </EditableSubmit>
                      <EditableCancel asChild>
                        <Button variant="ghost" size="sm" className="group/cancel h-6 w-6 p-0 transition-all">
                          <X className="h-3.5 w-3.5 text-muted-foreground group-hover/cancel:text-foreground transition-all" />
                        </Button>
                      </EditableCancel>
                    </div>
                  </Editable>
                  {errors?.slug && (
                    <span className="text-xs text-destructive">Workspace slug {errors.slug[0]}, please try again</span>
                  )}
                </div>
              </div>
              <div className="flex items-start justify-between px-4 py-3">
                <div className="flex items-start gap-1.5 text-muted-foreground">
                  <Building2 className="size-4 shrink-0 mt-0.5" aria-hidden />
                  <div className="flex flex-col gap-0.5">
                    <span className="flex items-center gap-1">
                      <span className="text-sm">Primary workspace</span>
                      <FieldTooltip ariaLabel="About the primary workspace">
                        <div className="mb-1 text-sm font-medium">How primary works</div>
                        <ul className="list-disc space-y-1 pl-5 text-[13px]">
                          <li>Primary workspaces can be used by emailing
                            <CopyableEmail email="new@app.decodekit.ai" /> and <CopyableEmail email="inbox@app.decodekit.ai" /> directly, or the workspace specific email addresses
                          </li>
                          <li>To switch your primary workspace, go to the desired workspace&apos;s settings and enable it</li>
                        </ul>
                      </FieldTooltip>
                    </span>
                  </div>
                </div>
                {workspace.primary ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center">
                        <Switch checked={true} disabled className="data-[state=checked]:bg-green-300" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>To change, enable primary on another workspace</TooltipContent>
                  </Tooltip>
                ) : (
                  <Switch
                    checked={false}
                    className="cursor-pointer"
                    onCheckedChange={handlePrimaryToggle}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="max-w-2xl">
            <h3 className="flex items-center gap-1.5 text-base font-medium mb-0.5">
              <Sparkles className="size-4 text-muted-foreground" aria-hidden />
              Agents
            </h3>
            <p className="text-sm text-muted-foreground mb-3">Agents configured in this workspace</p>
            {agents && agents.length > 0 ? (
              <div className="rounded-lg border divide-y">
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="group/row flex items-center justify-between gap-4 px-4 py-3 hover:bg-accent has-[[data-state=open]]:bg-accent transition-colors cursor-pointer"
                    onClick={() => router.visit(`/${workspace.slug}/agents/${agent.id}`)}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`size-2 rounded-full shrink-0 ${agentStatusColor(agent.status)}`} />
                        <span className="text-sm font-medium truncate">{agent.name || "Untitled agent"}</span>
                      </div>
                      <span className="text-xs text-muted-foreground capitalize pl-4">{agent.status}</span>
                    </div>

                    <div className="flex items-center gap-5 shrink-0">
                      <div className="flex items-center gap-5 text-sm text-muted-foreground">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1.5">
                              <Play className="size-3.5 shrink-0" />
                              {agent.runs_last_30_days} {agent.runs_last_30_days === 1 ? "run" : "runs"}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Runs in the last 30 days</TooltipContent>
                        </Tooltip>
                        {agent.success_rate_30_days !== null && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1.5">
                                <CheckCircle2 className="size-3.5 shrink-0" />
                                {agent.success_rate_30_days}%
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Success rate over the last 30 days</TooltipContent>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1.5">
                              <Clock className="size-3.5 shrink-0" />
                              {agent.last_run_at ? timeAgo(agent.last_run_at) : "Never"}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Last run</TooltipContent>
                        </Tooltip>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-white"
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <FolderInput className="size-4" />
                              Move to workspace
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {all_workspaces.map((ws) => {
                                const isCurrent = ws.id === workspace.id
                                return (
                                  <DropdownMenuItem
                                    key={ws.id}
                                    disabled={isCurrent}
                                    className={isCurrent ? "bg-accent opacity-100!" : ""}
                                    onSelect={() => setPendingMove({ agent, targetWorkspace: ws })}
                                  >
                                    {ws.name}
                                    {isCurrent && <Check className="ml-auto size-4 text-muted-foreground" />}
                                  </DropdownMenuItem>
                                )
                              })}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            onSelect={() => setPendingDelete(agent)}
                          >
                            <Trash2 className="size-4 text-destructive" />
                            Delete agent
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 px-4 py-3">
                <div className="text-sm text-muted-foreground flex items-center gap-x-1 gap-y-0.5 flex-wrap">
                  <span>Create your first agent by emailing</span>
                  <button
                    type="button"
                    onClick={handleCopyNewAgentAddress}
                    className="group/copy inline-flex items-center gap-1 text-xs font-medium bg-muted px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                  >
                    <span className="text-foreground break-all">{newAgentAddress}</span>
                    {copiedNewAgent ? (
                      <Check className="h-3 w-3 text-green-500 shrink-0" />
                    ) : (
                      <Copy className="h-3 w-3 text-muted-foreground group-hover/copy:text-foreground shrink-0 transition-colors" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="flex items-center gap-1.5 text-base font-medium mb-0.5">
                  <Users className="size-4 text-muted-foreground" aria-hidden />
                  Members
                </h3>
                <p className="text-sm text-muted-foreground">People with access to this workspace</p>
              </div>
              {canInvite && (
                <Button size="sm" variant="outline" className="group/invite" onClick={() => setInviteOpen(true)}>
                  <UserPlus className="size-4 text-muted-foreground group-hover/invite:text-foreground transition-colors" />
                  Invite member
                </Button>
              )}
            </div>
            {(memberships?.length > 0 || invitations?.length > 0) ? (
              <div className="rounded-lg border divide-y">
                {memberships.map((membership) => {
                  const isSelf = membership.user_id === current_user?.id
                  const canManage = canManageMembership(membership)
                  return (
                    <div key={membership.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="size-9 rounded-md [&>*]:rounded-md">
                          <AvatarImage src={membership.avatar_url} alt={membership.user_name || membership.user_email} />
                          <AvatarFallback>
                            {(membership.user_name || membership.user_email).slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-0.5">
                          {membership.user_name && (
                            <span className="flex items-center gap-2 font-medium text-sm">
                              {membership.user_name}
                              {isSelf && <Badge variant="sky">You</Badge>}
                            </span>
                          )}
                          <span className={`text-sm ${membership.user_name ? "text-muted-foreground text-xs" : "font-medium"}`}>{membership.user_email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-5">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          {(() => { const RoleIcon = roleIcon(membership.role); return <RoleIcon className="size-4 shrink-0" aria-hidden /> })()}
                          <span className="text-sm capitalize">{membership.role}</span>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <Shield className="size-4" />
                                  Change role
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  {[ "member", "admin", "owner" ].filter(canAssignRole).map((role) => (
                                    <DropdownMenuItem
                                      key={role}
                                      disabled={membership.role === role}
                                      className={membership.role === role ? "bg-accent opacity-100!" : ""}
                                      onSelect={() => handleRoleChange(membership.id, role)}
                                    >
                                      <span className="capitalize">{role}</span>
                                      {membership.role === role && <Check className="ml-auto size-4 text-muted-foreground" />}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                onSelect={() => setPendingRemoveMember(membership)}
                              >
                                <Trash2 className="size-4 text-destructive" />
                                Remove member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  )
                })}
                {invitations.map((invitation) => (
                  <div key={`inv-${invitation.id}`} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="size-9 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <Mail className="size-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-2 text-sm font-medium">
                          {invitation.email}
                          <Badge variant="warning">Invited</Badge>
                        </span>
                        <span className="text-xs text-amber-600">{formatExpiry(invitation.expires_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-5">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        {(() => { const RoleIcon = roleIcon(invitation.role); return <RoleIcon className="size-4 shrink-0" aria-hidden /> })()}
                        <span className="text-sm capitalize">{invitation.role}</span>
                      </div>
                      {canInvite && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => handleResendInvitation(invitation.id)}>
                              <Mail className="size-4" />
                              Resend invitation
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive focus:bg-destructive/10"
                              onSelect={() => handleCancelInvitation(invitation.id)}
                            >
                              <X className="size-4 text-destructive" />
                              Cancel invitation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No members in this workspace.</p>
            )}
          </div>

          <div className="max-w-2xl">
            <h3 className="flex items-center gap-1.5 text-base font-medium text-destructive mb-0.5">
              <LogOut className="size-4" aria-hidden />
              Leave this workspace
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {isLastOwner()
                ? "Transfer ownership to another member to leave"
                : "You'll lose access to this workspace and its agents"}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn("inline-flex", isLastOwner() && "cursor-not-allowed")}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                    disabled={isLastOwner()}
                    onClick={() => setPendingLeave(true)}
                  >
                    Leave workspace
                  </Button>
                </span>
              </TooltipTrigger>
              {isLastOwner() && (
                <TooltipContent>Transfer ownership to another member to leave</TooltipContent>
              )}
            </Tooltip>
          </div>

        </div>
      </ScrollArea>

      <Dialog open={!!pendingMove} onOpenChange={(open) => !open && setPendingMove(null)}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="py-4 px-6 gap-0.5">
            <DialogTitle className="text-lg font-medium mb-3">Move agent</DialogTitle>
            <DialogDescription>
              <p className="mb-4">Move <strong className="font-medium text-foreground">{pendingMove?.agent.name || "this agent"}</strong> to <strong className="font-medium text-foreground">{pendingMove?.targetWorkspace.name}</strong>?</p>
              <p>This agent will no longer appear in <strong className="font-medium text-foreground">{workspace.name}</strong>.</p>
            </DialogDescription>
          </div>
          <DialogFooter className="justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setPendingMove(null)}>Cancel</Button>
            <Button size="sm" onClick={confirmMove}>Move agent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteAgentDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        agentId={pendingDelete?.id}
        workspaceSlug={workspace.slug}
      />

      {/* Invite member dialog */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) inviteForm.reset() }}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Invite a member</DialogTitle>
            <DialogDescription>Send an invitation to join <strong className="font-medium text-foreground">{workspace.name}</strong></DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteSubmit}>
            <div className="p-6 py-4 space-y-3">
              <div className="space-y-1.5">
                <label htmlFor="invite-email" className="text-sm font-medium">Email address</label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteForm.data.email}
                  onChange={(e) => inviteForm.setData("email", e.target.value)}
                  autoComplete="off"
                />
                {inviteForm.errors.email && (
                  <p className="text-xs text-destructive">{inviteForm.errors.email}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label htmlFor="invite-role" className="text-sm font-medium">Role</label>
                <Select value={inviteForm.data.role} onValueChange={(val) => inviteForm.setData("role", val)}>
                  <SelectTrigger id="invite-role" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles().map((role) => (
                      <SelectItem key={role} value={role}>
                        <span className="capitalize">{role}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inviteForm.errors.role && (
                  <p className="text-xs text-destructive">{inviteForm.errors.role}</p>
                )}
              </div>
            </div>
            <DialogFooter className="justify-between gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={inviteForm.processing}>
                {inviteForm.processing ? "Sending…" : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove member confirmation dialog */}
      <Dialog open={!!pendingRemoveMember} onOpenChange={(open) => !open && setPendingRemoveMember(null)}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Remove member</DialogTitle>
            <DialogDescription>
              Remove <strong className="font-medium text-foreground">{pendingRemoveMember?.user_name || pendingRemoveMember?.user_email}</strong> from <strong className="font-medium text-foreground">{workspace.name}</strong>? They will lose access to this workspace and its agents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setPendingRemoveMember(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={confirmRemoveMember}>Remove member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave workspace confirmation dialog */}
      <Dialog open={pendingLeave} onOpenChange={(open) => setPendingLeave(open)}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Leave workspace</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave <strong className="font-medium text-foreground">{workspace.name}</strong>? You'll lose access to this workspace and its agents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-between gap-2">
            <Button variant="outline" size="sm" onClick={() => setPendingLeave(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={confirmLeave}>Leave workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  )
}
